import { flow } from '@core/utilities/lodash-utils';
import Product, { Option, Variant } from '@monolith/legacy/types/product';
import type { ESProductHit } from '@bc/discovery/types';
import { Item } from '@monolith/legacy/types/cart';
import { ProductsVariant } from '@monolith/legacy/types/api/brand-create-order';
import { Amount } from '@core/types/amount';
import { Currency } from 'dinero.js';
import ProductDraft from '@monolith/legacy/types/account/account-products/catalog-integration/product-integration';
import { GetOneCartStatusEnum } from '@client/api-internal/data-contracts';

export const isVariantOutOfStock = (variant: Variant | Option | ProductsVariant): boolean => {
  if (variant?.stock?.is_always_in_stock || variant?.stock?.inventory_policy === 'continue') {
    return false;
  }

  return variant?.stock?.status === GetOneCartStatusEnum.OutOfStock;
};

const isVariantLowInStock = (variant: Variant | Option): boolean => {
  return !variant?.stock?.is_always_in_stock && variant?.stock?.status === GetOneCartStatusEnum.LowInStock;
};

export const getVariantQuantity = (variant: Variant | Option): number => variant?.stock?.available_quantity;

export const getMaxQuantity = (availableQuantity: number, selectedQuantity: number, unitMultiplier: number) => {
  const maxQuantity = selectedQuantity * unitMultiplier * 2;

  // if the product is always in stock, or backordered via inventory_policy='continue'
  if (!availableQuantity || availableQuantity < 0 || (availableQuantity > 100 && selectedQuantity < 100)) {
    return Math.max(100, maxQuantity);
  } else if (availableQuantity <= 100) {
    return availableQuantity;
  } else {
    return Math.min(availableQuantity, maxQuantity);
  }
};

export const getListValuesForSelectInput = (alreadyInCart: boolean, maxLength: number, unitMultiplier: number): number[] => {
  const values = {} as number[];

  for (let i = alreadyInCart ? 0 : 1; i * unitMultiplier <= maxLength; i++) {
    values[i] = i * unitMultiplier;
  }

  return values;
};

export const isProductMultiple = (product: Product | ESProductHit): boolean => {
  return product.variants?.length > 1;
};

export const isProductOutOfStock = (product: Product | ESProductHit | ProductDraft): boolean => {
  // the explicit typing can be removed when the options are cleared from the repository, however right now it's
  // necessary due to a TypeScript bug: it doesn't recognise this is type-safe and inferred type is not enough.
  // see open issue and suggested workaround: https://github.com/microsoft/TypeScript/issues/44373#issuecomment-1405744410
  const variants: Array<Variant | Option> = product.variants?.length ? product.variants : product.options;
  return variants.every((variant) => isVariantOutOfStock(variant));
};

export const isProductLowInStock = (product: Product | ESProductHit): boolean => {
  // the explicit typing can be removed when the options are cleared from the repository, however right now it's
  // necessary due to a TypeScript bug: it doesn't recognise this is type-safe and inferred type is not enough.
  // see open issue and suggested workaround: https://github.com/microsoft/TypeScript/issues/44373#issuecomment-1405744410
  const variants: Array<Variant | Option> = product.variants?.length ? product.variants : product.options;
  return variants.every((v) => isVariantLowInStock(v));
};

export const getFirstAvailableVariant = (product: Product | ESProductHit): Variant | Option => {
  return product.variants.find((variant) => !isVariantOutOfStock(variant));
};

export const getMainVariant = (variants: Variant[]) => {
  return variants?.find(({ out_of_stock, options }) => !out_of_stock && options?.length)?.options[0];
};

export const getVariantValuesByOptionName = (variants: Variant[], optionName: string) => {
  const filteredOptionsValues = variants
    .map((variant) => variant.options.find(({ name }) => name === optionName)?.value)
    .filter(Boolean); // removed undefined values
  const uniqValues = new Set(filteredOptionsValues);
  return [...uniqValues];
};

export const getVariantOptionsLabel = (variants: Variant[], translateOptionName: (name: string) => string): string => {
  return (variants[0]?.options ?? []).map(({ name }) => translateOptionName(name)).join(' / ');
};

export const getVariantCombinedLabel = (variant: Variant): string => variant.options.map(({ value }) => value).join(' / ');

export const getFirstProductVariantImage = (
  product: Product | ESProductHit,
  selectedVariant: Variant = product.variants?.[0]
): string | undefined => {
  return selectedVariant?.images?.[0] ?? product.images[0];
};

export const getQuantityOptions = (item: Item) => {
  const selected = item.quantity;
  const availableQuantity = item.option.stock?.available_quantity;
  const unitMultiplier = item.option.unit_multiplier;
  const maxQuantity = getMaxQuantity(availableQuantity, selected, unitMultiplier);
  const values = [];

  for (let i = 0; i * unitMultiplier <= maxQuantity; i++) {
    const option = i * unitMultiplier;
    values[i] = {
      label: option,
      value: i,
    };
  }

  return values;
};

export const isMultipleProduct = (variants: Variant[]): boolean => {
  const hasDefaultVariant = variants.length === 1 && variants[0].options.length === 0;
  return !hasDefaultVariant && variants.length > 0;
};

const zeroAmount = (currency: Currency): Amount => ({
  amount: 0,
  currency: currency,
});

const multiplyAmount = (value: Amount, quantity: number): Amount => ({
  ...value,
  amount: value.amount * quantity,
});

/**
 * Resolves a variant's price fields, preferring the nested `price` object (populated on
 * fully-fetched products) and falling back to the variant's top-level fields (populated on
 * search/Elasticsearch hits). `original_wholesale_price` has no top-level equivalent, so it
 * falls back to the wholesale price.
 */
export const getVariantPriceFields = (
  variant: Variant
): { original_wholesale_price: Amount; wholesale_price: Amount; retail_price: Amount } => {
  const price = variant.price;
  return {
    original_wholesale_price: price?.original_wholesale_price ?? variant.wholesale_price,
    wholesale_price: price?.wholesale_price ?? price?.original_wholesale_price ?? variant.wholesale_price,
    retail_price: price?.retail_price ?? variant.retail_price,
  };
};

export const calculateTotalPrice = (price: Amount, quantity: number, unitMultiplier: number): Amount => {
  if (!quantity || !unitMultiplier) {
    return zeroAmount(price.currency);
  }

  const multiplyByQuantity = (value: Amount) => multiplyAmount(value, quantity);
  const multiplyByMultiplier = (value: Amount) => multiplyAmount(value, unitMultiplier);
  const calculateTotal = flow(multiplyByQuantity, multiplyByMultiplier);

  return calculateTotal(price);
};

export const getOutOfStockVariantsIds = (products: Product[]): number[] => {
  return products
    .filter((product) => isProductOutOfStock(product) && product.variants?.length)
    .map((product) => product.variants[0].id);
};
